import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Form,
  Button,
  Container,
  Spinner,
  Alert,
  Card,
  Row,
  Col,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import RecurringInvoiceFields from "./RecurringInvoiceFields";

const InvoiceEdit = ({
  invoice,
  customer,
  setRefresh,
  setModalShow,
  invoiceItems,
}) => {
  const [loading, setLoading] = useState(false);
  const [banksLoading, setBanksLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const [banks, setBanks] = useState([]);
  const navigate = useNavigate();

  function formatDate(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  const getInitialSelectedBankId = () => {
    if (invoice?.paymentDetails?.bankId) {
      return String(invoice.paymentDetails.bankId);
    }
    return "";
  };

  const [formData, setFormData] = useState({
    CustomerId: invoice.CustomerId,
    invoiceNumber: invoice.invoiceNumber || "",
    date: formatDate(invoice.date),
    dueDate: invoice.dueDate || "",
    isRecurring: Boolean(invoice.isRecurring),
    recurringFrequency: invoice.isRecurring
      ? invoice.recurringFrequency || "monthly"
      : "",
    recurringInterval: invoice.isRecurring ? invoice.recurringInterval || 1 : 1,
    recurringDayOfMonth: invoice.recurringDayOfMonth || "",
    desc: invoice.desc || "",
    items: invoiceItems?.length
      ? invoiceItems
      : [{ description: "", units: "", unitPrice: "" }],
    total: invoice.total || 0,
    selectedBankId: getInitialSelectedBankId(),
  });

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        setBanksLoading(true);
        const token = localStorage.getItem("token");

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const userBanks = response.data?.banks || [];
        setBanks(userBanks);

        setFormData((prev) => {
          if (prev.selectedBankId) return prev;

          const invoiceBankId = invoice?.paymentDetails?.bankId;
          if (invoiceBankId) {
            return {
              ...prev,
              selectedBankId: String(invoiceBankId),
            };
          }

          if (userBanks.length > 0) {
            return {
              ...prev,
              selectedBankId: String(userBanks[0].id),
            };
          }

          return prev;
        });
      } catch (error) {
        setErrMsg(
          error?.response?.data?.error || "Failed to load payment details",
        );
      } finally {
        setBanksLoading(false);
      }
    };

    fetchBanks();
  }, [invoice]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        [name]: value,
      };

      return {
        ...prev,
        items: newItems,
      };
    });
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { description: "", units: "", unitPrice: "" }],
    }));
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((total, item) => {
      const units = parseFloat(item.units) || 0;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      return total + units * unitPrice;
    }, 0);
  };

  const selectedBank = useMemo(() => {
    return banks.find(
      (bank) => String(bank.id) === String(formData.selectedBankId),
    );
  }, [banks, formData.selectedBankId]);

  const paymentPreview = useMemo(() => {
    if (selectedBank) {
      return {
        bankId: selectedBank.id,
        bankName: selectedBank.bankName || "",
        accountName: selectedBank.accountName || "",
        accountNumber: selectedBank.accountNumber || "",
        IBAN: selectedBank.IBAN || "",
        currency: selectedBank.currency || "",
      };
    }

    if (invoice?.paymentDetails) {
      return {
        bankId: invoice.paymentDetails.bankId || null,
        bankName: invoice.paymentDetails.bankName || "",
        accountName: invoice.paymentDetails.accountName || "",
        accountNumber: invoice.paymentDetails.accountNumber || "",
        IBAN: invoice.paymentDetails.IBAN || "",
        currency: invoice.paymentDetails.currency || "",
      };
    }

    return null;
  }, [selectedBank, invoice]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrMsg("");

    try {
      const token = localStorage.getItem("token");
      const total = calculateTotal();

      await axios.put(
        `${process.env.REACT_APP_API_URL}/invoices/update/${invoice.id}`,
        {
          ...formData,
          total,
          paymentDetails: paymentPreview,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setLoading(false);
      setModalShow(false);
      navigate(`/invoice/${invoice.id}`);
      setRefresh(true);
    } catch (error) {
      setErrMsg(error?.response?.data?.error || "Failed to update invoice");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container
        style={{ height: "100vh", fontSize: "250px" }}
        className="d-flex justify-content-center align-items-center"
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: "700px" }}>
      <h1>Update Invoice</h1>

      {errMsg && <Alert variant="danger">{errMsg}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Form.Group>
          <Form.Label>Customer</Form.Label>
          <Form.Control type="text" value={customer.name} readOnly required />
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>Due Date</Form.Label>
              <Form.Control
                type="text"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group>
          <Form.Label>Invoice Number</Form.Label>
          <Form.Control
            type="text"
            name="invoiceNumber"
            value={formData.invoiceNumber}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>Invoice Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="desc"
            value={formData.desc}
            onChange={handleChange}
          />
        </Form.Group>

        <RecurringInvoiceFields
          formData={formData}
          setFormData={setFormData}
          idPrefix="edit-invoice"
        />

        <Card className="mt-3 mb-3">
          <Card.Body>
            <h5 className="mb-3">Payment Details</h5>

            <Form.Group>
              <Form.Label>Select Bank Account</Form.Label>
              <Form.Select
                name="selectedBankId"
                // value={formData.selectedBankId}
                onChange={handleChange}
                disabled={banksLoading || banks.length === 0}
              >
                <option value="">
                  {banks.length === 0
                    ? "No saved bank accounts"
                    : "Keep current payment details"}
                </option>

                {banks.map((bank) => (
                  <option key={bank.id} value={bank.id}>
                    {bank.bankName} - {bank.accountName} ({bank.currency})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {paymentPreview && (
              <div className="mt-3">
                <p className="mb-1">
                  <strong>Bank:</strong> {paymentPreview.bankName || "-"}
                </p>
                <p className="mb-1">
                  <strong>Account Name:</strong>{" "}
                  {paymentPreview.accountName || "-"}
                </p>
                <p className="mb-1">
                  <strong>Account Number:</strong>{" "}
                  {paymentPreview.accountNumber || "-"}
                </p>
                <p className="mb-1">
                  <strong>IBAN/SWIFT:</strong> {paymentPreview.IBAN || "-"}
                </p>
                <p className="mb-0">
                  <strong>Currency:</strong> {paymentPreview.currency || "-"}
                </p>
              </div>
            )}
          </Card.Body>
        </Card>

        {formData.items.map((item, index) => (
          <div key={index} style={{ marginBottom: "1rem" }}>
            <Form.Group>
              <Form.Label>Item Description</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={item.description}
                onChange={(e) => handleItemChange(index, e)}
                required
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Units</Form.Label>
              <Form.Control
                type="number"
                step="any"
                name="units"
                value={item.units}
                min={0}
                onChange={(e) => handleItemChange(index, e)}
                required
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Unit Price</Form.Label>
              <Form.Control
                type="number"
                name="unitPrice"
                value={item.unitPrice}
                onChange={(e) => handleItemChange(index, e)}
                required
                step="any"
                min={0}
              />
            </Form.Group>

            {index > 0 && (
              <Button
                variant="danger"
                type="button"
                onClick={() => removeItem(index)}
              >
                Remove Item
              </Button>
            )}
          </div>
        ))}

        <Button variant="primary" type="button" onClick={addItem}>
          Add Item
        </Button>

        <Form.Group>
          <Form.Label>Total</Form.Label>
          <Form.Control type="text" value={calculateTotal()} readOnly />
        </Form.Group>

        <Button className="mt-3" type="submit">
          Update Invoice
        </Button>
      </Form>
    </Container>
  );
};

export default InvoiceEdit;
